import { SiteWithModuleStatusDto } from "@models/site-with-module-status";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { useNavigate } from "react-router";
import { classes } from "../../../helper";
import { usePinnedSites } from "../../../hooks/usePinnedSites";
import { useSelectedSites } from "../../../hooks/useSelectedSites";
import { useWindowSize } from "../../../hooks/useWindowSize";
import Button from "../../Button/Button";
import Checkbox from "../../Checkbox/Checkbox";
import Icon from "../../Icon/Icon";
import Switch from "../../Switch/Switch";
import AllSitesPill from "./AllSitesPill";
import { MAX_PINS, MODULE, PIN_MIN } from "./constants";
import { getAllSitesSelection } from "./getAllSitesSelection";
import { getSiteColumns } from "./getSiteColumns";
import styles from "./GridContent.module.css";
import PinButton from "./PinButton";
import ProductionAreaTabs from "./ProductionAreaTabs";
import SiteNav from "./SiteNav";
import shared from "./SiteSelector.module.css";
import { ContentProps } from "./types";

const VIEWPORT_MARGIN = 16;

function GridContent({
  site,
  sites,
  productionAreaTabs,
  activeProductionArea,
  setActiveProductionArea,
  setIsOpen,
  getSiteUrl,
  allowMultiSite = true,
  getMultiSiteUrl,
}: ContentProps) {
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const { pinned, togglePin } = usePinnedSites();
  const { selectedSites, setSelected, isMultiSiteMode } = useSelectedSites();

  const navigate = useNavigate();

  const { width: windowWidth } = useWindowSize();

  const panelRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(
    windowWidth - 2 * VIEWPORT_MARGIN
  );
  useLayoutEffect(() => {
    if (panelRef.current) {
      // The right-aligned dropdown ends at its trigger, not the viewport edge.
      setAvailableWidth(
        Math.max(
          0,
          Math.min(
            windowWidth - 2 * VIEWPORT_MARGIN,
            panelRef.current.getBoundingClientRect().right - VIEWPORT_MARGIN
          )
        )
      );
    }
  }, [windowWidth]);

  const [draft, setDraft] = useState<string[]>(selectedSites);
  const [isSelectingMultiple, setIsSelectingMultiple] = useState(false);
  const selectMultiple = isSelectingMultiple || isMultiSiteMode;

  const toggleSelectMultiple = () => {
    if (!selectMultiple) {
      setDraft(selectedSites);
      setIsSelectingMultiple(true);
    } else {
      const firstSelected = selectedSites.slice(0, 1);
      setDraft(firstSelected);
      setSelected(firstSelected);
      setIsSelectingMultiple(false);
    }
  };

  const toggleDraft = (siteName: string) =>
    setDraft((prev) =>
      prev.includes(siteName)
        ? prev.filter((s) => s !== siteName)
        : [...prev, siteName]
    );

  const close = () => setIsOpen(false);
  const pinningEnabled = sites.length >= PIN_MIN;

  const handleSelect = (siteName: string) => {
    setSelected([siteName]);
    close();
  };

  const applySelection = () => {
    if (getMultiSiteUrl) {
      navigate(getMultiSiteUrl(draft));
      close();
      return;
    }

    setSelected(draft);
    setIsSelectingMultiple(false);
    close();
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = sites;
    return term
      ? list.filter((p) => p.site.toLowerCase().includes(term))
      : list;
  }, [sites, search]);

  const pinnedSites = pinningEnabled
    ? pinned
        .map((name) => filtered.find((p) => p.site === name))
        .filter((p): p is SiteWithModuleStatusDto => p !== undefined)
    : [];

  const unpinnedSites = pinningEnabled
    ? filtered.filter((p) => !pinned.includes(p.site))
    : filtered;

  // Size the grid for the largest production area tab so switching tabs doesn't resize the panel.
  const layoutSiteCount = useMemo(
    () =>
      productionAreaTabs.reduce(
        (max, tab) => Math.max(max, tab.siteCount),
        sites.length
      ),
    [productionAreaTabs, sites.length]
  );

  const columns = getSiteColumns(
    unpinnedSites,
    availableWidth,
    layoutSiteCount
  );

  return (
    <div
      ref={panelRef}
      className={styles.panel}
      style={{ maxWidth: availableWidth }}
    >
      <div className={styles.panelHeader}>
        <div className={styles.headerBar}>
          <div className={styles.headerTitleWrap}>
            <ProductionAreaTabs
              tabs={productionAreaTabs}
              activeProductionArea={activeProductionArea}
              onSelect={setActiveProductionArea}
            />
          </div>
          <div className={styles.headerControls}>
            {allowMultiSite && (
              <Switch
                label="Select multiple"
                checked={selectMultiple}
                toggleChecked={toggleSelectMultiple}
              />
            )}
            {searchOpen ? (
              <div className={styles.searchOpen}>
                <Icon name="Search" className={styles.searchIcon} />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onBlur={() => {
                    if (!search) setSearchOpen(false);
                  }}
                />
              </div>
            ) : (
              <Button
                type="button"
                variant="transparent"
                className={styles.searchButton}
                aria-label="Search sites"
                onClick={() => setSearchOpen(true)}
              >
                <Icon name="Search" className={styles.searchIcon} />
              </Button>
            )}
          </div>
        </div>

        {pinningEnabled ? (
          <div className={styles.quickSelect}>
            <div className={styles.pinnedArea}>
              <span className={styles.quickSelectLabel}>Quick select</span>
              <AllSitesPill
                variant="pill"
                disabled={!allowMultiSite}
                onSelectAll={() => {
                  setSelected(getAllSitesSelection(sites));
                  close();
                }}
              />
              {pinnedSites.length > 0
                ? pinnedSites.map((p) => (
                    <div key={p.site} className={styles.siteChip}>
                      {selectMultiple ? (
                        <span className={styles.siteChipCheckbox}>
                          <Checkbox
                            text={p.site}
                            checked={draft.includes(p.site)}
                            toggleChecked={() => toggleDraft(p.site)}
                          />
                        </span>
                      ) : getSiteUrl ? (
                        <SiteNav
                          to={getSiteUrl(p)}
                          onClick={() => {
                            handleSelect(p.site);
                            navigate(getSiteUrl(p));
                          }}
                          className={styles.siteChipLink}
                        >
                          <span className={shared.siteName}>{p.site}</span>
                        </SiteNav>
                      ) : (
                        <div
                          className={styles.siteChipLink}
                          onClick={() => handleSelect(p.site)}
                        >
                          <span className={shared.siteName}>{p.site}</span>
                        </div>
                      )}
                      <PinButton
                        active
                        siteName={p.site}
                        onToggle={() => togglePin(p.site)}
                      />
                    </div>
                  ))
                : !searchOpen && (
                    <span className={styles.pinHint}>
                      Pin your top 6 sites for quick access
                    </span>
                  )}
            </div>
          </div>
        ) : (
          <div className={styles.simpleBar}>
            <AllSitesPill
              variant="pill"
              disabled={!allowMultiSite}
              onSelectAll={() => {
                setSelected(getAllSitesSelection(sites));
                close();
              }}
            />
          </div>
        )}
      </div>

      <div className={styles.grid}>
        {columns.length > 0 && columns[0].length > 0 ? (
          columns.map((column, index) => (
            <div key={index} className={styles.gridColumn}>
              {column.map((p) => {
                const isCurrent =
                  selectedSites.includes(p.site) || p.site === site;
                const rowClass = classes(
                  styles.gridRow,
                  isCurrent ? styles.gridRowCurrent : ""
                );

                const pin =
                  pinningEnabled && p.moduleEnabled ? (
                    <PinButton
                      active={pinned.includes(p.site)}
                      disabled={
                        !pinned.includes(p.site) && pinned.length >= MAX_PINS
                      }
                      siteName={p.site}
                      onToggle={() => togglePin(p.site)}
                    />
                  ) : null;

                if (!p.moduleEnabled) {
                  return (
                    <div
                      key={p.site}
                      className={classes(rowClass, shared.disabled)}
                      title={`${p.site} does not have ${MODULE} enabled.`}
                    >
                      {selectMultiple ? (
                        <Checkbox text={p.site} checked={false} disabled />
                      ) : (
                        <span className={shared.siteName}>{p.site}</span>
                      )}
                      {pin}
                    </div>
                  );
                }

                if (selectMultiple) {
                  return (
                    <div key={p.site} className={rowClass}>
                      <Checkbox
                        text={p.site}
                        checked={draft.includes(p.site)}
                        toggleChecked={() => toggleDraft(p.site)}
                      />
                      {pin}
                    </div>
                  );
                }

                return getSiteUrl ? (
                  <SiteNav
                    key={p.site}
                    to={getSiteUrl(p)}
                    onClick={() => {
                      handleSelect(p.site);
                      navigate(getSiteUrl(p));
                    }}
                    className={rowClass}
                  >
                    <span className={shared.siteName}>{p.site}</span>
                    {pin}
                  </SiteNav>
                ) : (
                  <div
                    key={p.site}
                    className={rowClass}
                    onClick={() => handleSelect(p.site)}
                  >
                    <span className={shared.siteName}>{p.site}</span>
                    {pin}
                  </div>
                );
              })}
            </div>
          ))
        ) : (
          <div className={styles.noSites}>No sites found</div>
        )}
      </div>

      {selectMultiple && (
        <div className={styles.bottomRow}>
          <span className={styles.selectedCount}>
            {draft.length} site{draft.length === 1 ? "" : "s"} selected
          </span>
          <Button
            type="button"
            variant="secondary"
            size="small"
            text="Apply"
            onClick={applySelection}
          />
        </div>
      )}
    </div>
  );
}

export default GridContent;
